import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTabMemory } from '../contexts/TabMemoryContext';

/**
 * Active module tab kept in the URL (?tab=…, and ?sub=… for pages with sub-tabs).
 *
 * - The URL wins: a reload, a shared link or the browser's Back/Forward open that tab.
 * - Without a valid tab in the URL (F-key, header) the module opens on the remembered tab.
 * - Switching tabs writes the URL (replace: no history entry per click) and remembers the tab.
 *
 * Only an explicit switch writes the URL or the memory. App.js swaps modules by state, so a module can
 * render once with the previous module's URL; writing from an effect there would land on the wrong page.
 *
 * @param {string} memoryKey - TabMemory key of the module (e.g. 'sales')
 * @param {string[]} tabIds - valid tab ids
 * @param {string} defaultTab - tab used when neither the URL nor the memory has a valid one
 * @param {(tabId: string) => string[]} [subTabsOf] - valid sub-tab ids of a tab (first = default)
 * @returns {{ activeTab: string, activeSubTab: string|null, setActiveTab: (tab: string, sub?: string) => void }}
 */
export default function useUrlTab(memoryKey, tabIds, defaultTab, subTabsOf = null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { rememberTab, getRememberedTab } = useTabMemory();

  const resolveSub = (tab, sub) => {
    if (!subTabsOf) return null;
    const subs = subTabsOf(tab) || [];
    return sub && subs.includes(sub) ? sub : (subs[0] || null);
  };

  const urlTab = searchParams.get('tab');
  let activeTab;
  let activeSubTab;
  if (urlTab && tabIds.includes(urlTab)) {
    activeTab = urlTab;
    activeSubTab = resolveSub(urlTab, searchParams.get('sub'));
  } else {
    const remembered = getRememberedTab(memoryKey, defaultTab);
    activeTab = tabIds.includes(remembered) ? remembered : defaultTab;
    activeSubTab = resolveSub(activeTab, subTabsOf ? getRememberedTab(`${memoryKey}:sub`, null) : null);
  }

  const setActiveTab = useCallback((tab, sub) => {
    if (!tabIds.includes(tab)) return;
    const nextSub = resolveSub(tab, sub);
    rememberTab(memoryKey, tab);
    if (subTabsOf) rememberTab(`${memoryKey}:sub`, nextSub);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      if (nextSub) next.set('sub', nextSub); else next.delete('sub');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey, rememberTab, setSearchParams, tabIds.join('|')]);

  return { activeTab, activeSubTab, setActiveTab };
}
